/**
 * PromptListEditor — script-editable field list for `open_intake_form` /
 * `open_property_intake_form` primitives in the Status Workflows Admin Studio.
 *
 * Serialises to / from the `prompts:` param — the exact same "field_key:
 * prompt text\n..." format the Runner already reads. That means:
 *   - reordering here reorders the runner's rendered form
 *   - editing prompt text here changes what the operator sees on-screen
 *   - removing a line hides the field from the runner
 *   - adding via the "+ Add Field" dropdown pulls unused backend fields from
 *     the Overwatch Field Registry
 *
 * Required fields (locked in the registry) render with 🔒 and cannot be
 * removed, but their prompt text is still editable.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Lock, ArrowUp, ArrowDown, X, Plus } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function parsePrompts(text) {
  if (!text) return [];
  const out = [];
  text.split('\n').forEach(line => {
    const m = line.match(/^\s*([a-z0-9_]+)\s*:\s*(.*)$/i);
    if (m) out.push({ key: m[1], prompt: (m[2] || '').trim() });
  });
  return out;
}

function serialisePrompts(items) {
  return items.map(i => `${i.key}: ${i.prompt}`).join('\n');
}

export default function PromptListEditor({ value, onChange, category = 'client', testidPrefix = 'prompts' }) {
  const items = useMemo(() => parsePrompts(value), [value]);
  const [registry, setRegistry] = useState({});
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [customKey, setCustomKey] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/overwatch/fields', { params: { category } })
      .then(res => {
        if (cancelled) return;
        const map = {};
        (res.data?.fields || []).forEach(f => { map[f.key] = f; });
        setRegistry(map);
      })
      .catch(() => setRegistry({}));
    return () => { cancelled = true; };
  }, [category]);

  const commit = (nextItems) => onChange(serialisePrompts(nextItems));

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = items.slice();
    const tmp = arr[idx]; arr[idx] = arr[j]; arr[j] = tmp;
    commit(arr);
  };
  const remove = (idx) => {
    const arr = items.slice();
    arr.splice(idx, 1);
    commit(arr);
  };
  const editPrompt = (idx, text) => {
    const arr = items.slice();
    arr[idx] = { ...arr[idx], prompt: text };
    commit(arr);
  };
  const addField = (key) => {
    if (!key) return;
    if (items.find(i => i.key === key)) return;    // already present
    const meta = registry[key] || {};
    const arr = items.concat([{ key, prompt: meta.default_prompt || '' }]);
    commit(arr);
    setAddPickerOpen(false);
    setCustomKey('');
  };

  const usedKeys = new Set(items.map(i => i.key));
  const availableKeys = Object.keys(registry).filter(k => !usedKeys.has(k));

  return (
    <div className="bg-white border border-slate-200 rounded p-2" data-testid={testidPrefix}>
      {items.length === 0 && (
        <div className="text-xs text-slate-500 italic px-2 py-3">No fields yet. Click &quot;+ Add Field&quot; below.</div>
      )}
      {items.map((item, idx) => {
        const meta = registry[item.key] || {};
        const isLocked = !!meta.required;
        const isUnknown = !registry[item.key];
        return (
          <div key={idx} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0"
            data-testid={`${testidPrefix}-row-${idx}`}>
            <div className="w-6 flex items-center justify-center">
              {isLocked ? (
                <span title="Required field — cannot be removed">
                  <Lock size={12} className="text-amber-500" />
                </span>
              ) : <span className="w-3 h-3" />}
            </div>
            <div className="w-36 shrink-0 text-xs font-mono text-slate-600 truncate" title={item.key}>
              {item.key}
              {isUnknown && <span className="ml-1 text-rose-500" title="Not in registry">⚠</span>}
            </div>
            <div className="w-24 shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
              {meta.type || '?'}
            </div>
            <input value={item.prompt}
              onChange={e => editPrompt(idx, e.target.value)}
              placeholder={meta.default_prompt || 'Question script for this field…'}
              data-testid={`${testidPrefix}-prompt-${idx}`}
              className="flex-1 text-xs px-2 py-1 border border-slate-200 rounded" />
            <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
              data-testid={`${testidPrefix}-up-${idx}`}
              title="Move up"
              className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-20">
              <ArrowUp size={12} />
            </button>
            <button type="button" onClick={() => move(idx, +1)} disabled={idx === items.length - 1}
              data-testid={`${testidPrefix}-down-${idx}`}
              title="Move down"
              className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-20">
              <ArrowDown size={12} />
            </button>
            <button type="button" onClick={() => remove(idx)} disabled={isLocked}
              data-testid={`${testidPrefix}-remove-${idx}`}
              title={isLocked ? 'Required — cannot remove' : 'Remove field'}
              className="p-1 rounded hover:bg-rose-50 text-rose-500 disabled:opacity-20">
              <X size={12} />
            </button>
          </div>
        );
      })}

      <div className="mt-2 pt-2 border-t border-slate-100">
        {!addPickerOpen ? (
          <button type="button" onClick={() => setAddPickerOpen(true)}
            data-testid={`${testidPrefix}-add-toggle`}
            className="text-xs px-3 py-1 rounded border border-slate-300 bg-slate-50 hover:bg-slate-100 flex items-center gap-1">
            <Plus size={12} /> Add Field
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <select onChange={e => { if (e.target.value) addField(e.target.value); }}
              defaultValue=""
              data-testid={`${testidPrefix}-add-picker`}
              className="text-xs px-2 py-1 border border-slate-300 rounded bg-white">
              <option value="">— pick a field —</option>
              {availableKeys.map(k => {
                const m = registry[k];
                return <option key={k} value={k}>{m.label} ({k})</option>;
              })}
            </select>
            <span className="text-[10px] text-slate-400">or</span>
            <input value={customKey} onChange={e => setCustomKey(e.target.value)}
              placeholder="custom_field_key"
              data-testid={`${testidPrefix}-add-custom`}
              className="text-xs px-2 py-1 border border-slate-300 rounded font-mono w-40" />
            <button type="button" onClick={() => addField(customKey.trim())}
              disabled={!customKey.trim()}
              data-testid={`${testidPrefix}-add-custom-btn`}
              className="text-xs px-2 py-1 rounded bg-slate-800 text-white disabled:opacity-30">
              Add
            </button>
            <button type="button" onClick={() => { setAddPickerOpen(false); setCustomKey(''); }}
              className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700">
              Cancel
            </button>
            <div className="w-full text-[10px] text-slate-500 mt-1">
              Custom fields must already exist on the backend Client/Property model —
              otherwise the value will be silently dropped on save.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
